import { CILogger } from 'common/log/log.provider';

import type { OktaSessionOutcome } from './fetchOktaSession';
import { rejectAfterTimeout } from './rejectAfterTimeout';

export const OKTA_SESSION_CHECK_DATABASE_NAME = 'oktaSessionCheck';

const DATABASE_VERSION = 1;
const STORE_NAME = 'checks';
const RECORD_KEY = 'last';

/** The store is read and written while holding the cross-tab lock, so a stuck call must not hold it for long. */
export const SHARED_CHECK_STORAGE_TIMEOUT_MS = 1000;

const OUTCOMES: OktaSessionOutcome[] = ['active', 'inactive', 'ambiguous'];

/**
 * The last session check any tab made. It carries no login: all tabs share one portal user, and only the tab that
 * asked Okta compares the Okta login with it.
 */
export interface SharedOktaSessionCheck {
    completedAt: number;
    outcome: OktaSessionOutcome;
    expiresAt?: string;
    /** HTTP status of an ambiguous answer, when Okta answered at all. */
    httpStatus?: number;
}

let database: Promise<IDBDatabase> | null = null;

/**
 * Reads the last check any tab saved, or `null` when there is none or it cannot be read.
 *
 * The check lives in IndexedDB rather than localStorage: a localStorage write reaches the other tabs some
 * milliseconds later, so a tab that gets the lock right after another tab saved its answer can still read the old
 * one. IndexedDB keeps one copy for the whole browser, so a write committed before the lock is released is what the
 * next holder reads.
 */
export async function readSharedCheck(): Promise<SharedOktaSessionCheck | null> {
    try {
        const stored = await rejectAfterTimeout(
            runInStore('readonly', (store) => store.get(RECORD_KEY) as IDBRequest<unknown>),
            SHARED_CHECK_STORAGE_TIMEOUT_MS,
            'Reading the shared Okta session check'
        );

        return isSharedCheck(stored) ? stored : null;
    } catch (error) {
        forgetDatabase();
        CILogger.getInstance().warn('Okta session check: the shared check could not be read', error);

        return null;
    }
}

/** Saves the check for the other tabs, resolving once the write is committed. */
export async function writeSharedCheck(check: SharedOktaSessionCheck): Promise<void> {
    try {
        await rejectAfterTimeout(
            runInStore('readwrite', (store) => store.put(check, RECORD_KEY)),
            SHARED_CHECK_STORAGE_TIMEOUT_MS,
            'Saving the shared Okta session check'
        );
    } catch (error) {
        forgetDatabase();
        CILogger.getInstance().warn('Okta session check: the shared check could not be saved', error);
    }
}

async function runInStore<T>(
    mode: IDBTransactionMode,
    makeRequest: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
    const db = await openDatabase();

    return new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode);
        const request = makeRequest(transaction.objectStore(STORE_NAME));

        // Waiting for the commit, not just the request, is what makes a write visible to the next lock holder.
        transaction.oncomplete = () => resolve(request.result);
        transaction.onerror = () => reject(transaction.error ?? request.error);
        transaction.onabort = () => reject(transaction.error ?? new Error('The IndexedDB transaction was aborted'));
    });
}

function openDatabase(): Promise<IDBDatabase> {
    database ??= new Promise<IDBDatabase>((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('This browser has no IndexedDB'));

            return;
        }

        const request = indexedDB.open(OKTA_SESSION_CHECK_DATABASE_NAME, DATABASE_VERSION);

        request.onupgradeneeded = () => {
            request.result.createObjectStore(STORE_NAME);
        };
        request.onsuccess = () => {
            const db = request.result;

            // Let another tab upgrade the database instead of blocking it, and reopen on the next check.
            db.onversionchange = () => {
                db.close();
                forgetDatabase();
            };
            db.onclose = forgetDatabase;
            resolve(db);
        };
        request.onerror = () => reject(request.error);
    });

    return database;
}

function forgetDatabase(): void {
    database = null;
}

function isSharedCheck(value: unknown): value is SharedOktaSessionCheck {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const { completedAt, outcome, expiresAt, httpStatus } = value as Partial<
        Record<keyof SharedOktaSessionCheck, unknown>
    >;

    return (
        typeof completedAt === 'number' &&
        OUTCOMES.includes(outcome as OktaSessionOutcome) &&
        (expiresAt === undefined || typeof expiresAt === 'string') &&
        (httpStatus === undefined || typeof httpStatus === 'number')
    );
}
